import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mantine/core';
import { IconArrowNarrowLeft } from '@tabler/icons-react';
import Page from '@/components/Page';
import { yearsToMonths } from '@/helpers/date';
import routes from '@/routes';
import { updateUser } from '@/data/features/user/user';
import { roundNumber, sliceToDecimals } from '@/helpers/number';
import { getMutationResult } from '@/helpers/query';
import { formatNumberWithCommas } from '@/helpers/string';
import { useGetPaymentLinkMutation } from '@/services/api';
import TableSimple from '@/components/TableSimple';
import BorrowerDetailsTable from '@/components/BorrowerDetailsTable';
import DataSummaryCard from '@/components/DataSummaryCard';
import classes from './summary.module.css';

const Summary = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [apiGetPaymentLink] = useGetPaymentLinkMutation();
  const proposal = useSelector((state) => state.offer);
  const {
    id, borrower: borrowersStore, isRecycling, mixes, cycleMortgage, loanAmount, loan: loanObj,
  } = proposal;
  const navigate = useNavigate();

  const showRealPaymentPage = import.meta.env.VITE_APP_SHOW_REAL_PAYMENT_PAGE === 'true';

  const disposalboard = mixes;
  let boxesTitles = {
    boxA: '',
    boxB: '',
  };

  const handleContinue = async () => {
    try {
      if (showRealPaymentPage) {
        const paymentData = {
          offerId: id,
        };
        const { data, isError } = await getMutationResult(paymentData, apiGetPaymentLink);

        if (isError) {
          notifications.show({
            title: t('globals.error.title'),
            message: t('globals.error.message'),
          });
          return;
        }
        dispatch(updateUser({ agreeToTerms: true, linkToPay: data.data.Url }));

        window.location.href = data.data.Url;
      } else {
        navigate(routes.pay.path);
      }
    } catch (error) {
      notifications.show({
        title: t('globals.error.title'),
        message: t('globals.error.message'),
      });
    }
  };

  const sumOfReturnsOfFirstOption = disposalboard?.[0].totalReturn;
  const sumOfReturnsOfSecondOption = disposalboard?.[1]
    ? disposalboard[1].totalReturn : 0;

  const sumOfReturnsOfInterestFirstOption = disposalboard?.[0]?.totalInterestReturn;
  const sumOfReturnsOfInterestSecondOption = disposalboard?.[1] ? disposalboard?.[1]?.totalInterestReturn : '';
  let updatedLoanAmount = loanAmount;
  let updatedLoanPeriod = yearsToMonths(proposal?.period);

  if (isRecycling) {
    let loansTocycle = cycleMortgage?.loans?.filter((
      loan,
    ) => loan.isBetterToChange === false) ?? [];

    if (loanObj.title === 'anyPurposeWithPropertyCollateral') {
      loansTocycle = cycleMortgage?.loans?.filter((
        loan,
      ) => loan.isBetterToChange === false || loan.loanPurpose === 'דיור') ?? [];
    }
    updatedLoanAmount += loansTocycle.reduce((sum, loan) => sum + (loan?.totalAmount ?? 0), 0);
    updatedLoanPeriod = mixes?.[0]?.disposalBoard.length;
  }

  const details = [
    {
      label: t('pages.summary.label.loanType'),
      value: proposal?.loan?.hebrew,
    },
    {
      label: t('pages.summary.label.loanAmount'),
      value: `${formatNumberWithCommas(roundNumber(updatedLoanAmount))} ₪`,
    },
    isRecycling && {
      label: t('pages.summary.label.currentLoanAmount'),
      value: proposal?.currentLoanAmount,
    },
    {
      label: t('pages.summary.label.paymentPeriodInMonths'),
      value: updatedLoanPeriod,
    },
    {
      label: t('pages.summary.label.borrowerNumber'),
      value: borrowersStore.length,
    },
  ].filter(Boolean);

  const planADetails = [
    `${formatNumberWithCommas(roundNumber(updatedLoanAmount))} ₪`,
    `${formatNumberWithCommas(roundNumber(disposalboard?.[0]?.disposalBoard[0]?.totalMonthlyReturn))} ₪`,
    `${formatNumberWithCommas(roundNumber(disposalboard?.[0]?.maxMonthlyReturn))} ₪`,
    `${formatNumberWithCommas(roundNumber(sumOfReturnsOfInterestFirstOption))} ₪`,
    `${formatNumberWithCommas(roundNumber(sumOfReturnsOfFirstOption))} ₪`,
    `${sliceToDecimals(sumOfReturnsOfFirstOption / updatedLoanAmount, 2)} ₪`,
  ];

  const planBDetails = [
    `${formatNumberWithCommas(roundNumber(updatedLoanAmount))} ₪`,
    `${formatNumberWithCommas(roundNumber(disposalboard?.[1]?.disposalBoard[0]?.totalMonthlyReturn))} ₪`,
    `${formatNumberWithCommas(roundNumber(disposalboard?.[1]?.maxMonthlyReturn))} ₪`,
    `${formatNumberWithCommas(roundNumber(sumOfReturnsOfInterestSecondOption))} ₪`,
    `${formatNumberWithCommas(roundNumber(sumOfReturnsOfSecondOption))} ₪`,
    `${sliceToDecimals(disposalboard?.[1]?.returnConversion, 2)} ₪`,
  ];

  const plansTableHead = [
    t('pages.summary.label.loanAmount'),
    t('pages.summary.label.paymentPerMonth'),
    t('pages.summary.label.maxPaymentPerMonth'),
    t('pages.summary.label.sumOfReturnsOfInterest'),
    t('pages.summary.label.totalPaymentInPeriodEnd'),
    t('pages.summary.label.forEvryShekelYouWillPay'),
  ];

  const tableHead = [
    t('pages.summary.table.head.month'),
    t('pages.summary.table.head.remaining'),
    t('pages.summary.table.head.monthlyPaymentWithoutInterest'),
    t('pages.summary.table.head.monthlyPaymentInterest'),
    t('pages.summary.table.head.totalPayment'),
  ];

  const risk0 = disposalboard?.[0]?.risk ?? -1; // Default to -1 if undefined
  const risk1 = disposalboard?.[1]?.risk ?? -1; // Default to -1 if undefined

  if (disposalboard?.length === 1) {
    // Single-element cases
    boxesTitles = {
      boxATitle: 'פרטי התוכנית המומלצת עבורך',
      boxBTitle: 'פרטי התוכנית בסיכון גבוה יותר',
    };
  } else {
    // Multi-element cases
    boxesTitles = {
      boxATitle:
        risk0 === 0 ? t('pages.offer.box.title.regular')
          : risk0 === 1 ? t('pages.offer.box.title.medium')
            : risk0 === 2 ? t('pages.offer.box.title.high')
              : '',
      boxBTitle:
        risk1 === 0 ? t('pages.offer.box.title.regular')
          : risk1 === 1 ? t('pages.offer.box.title.medium')
            : risk1 === 2 ? t('pages.offer.box.title.high')
              : '',
    };
  }

  const tableBodyA = mixes?.[0]?.disposalBoard?.map((item) => [
    item?.period,
    `${formatNumberWithCommas(roundNumber(item.fundBalance)) || 0} ₪`,
    `${formatNumberWithCommas(roundNumber(item.monthlyFundReturn))} ₪`,
    `${formatNumberWithCommas(roundNumber(item?.monthlyInterestReturn))} ₪`,
    `${formatNumberWithCommas(roundNumber(item?.totalMonthlyReturn))} ₪`,
  ]);

  const tableBodyB = mixes?.[1]?.disposalBoard?.map((item) => [
    item?.period,
    `${formatNumberWithCommas(roundNumber(item.fundBalance)) || 0} ₪`,
    `${formatNumberWithCommas(roundNumber(item.monthlyFundReturn))} ₪`,
    `${formatNumberWithCommas(roundNumber(item?.monthlyInterestReturn))} ₪`,
    `${formatNumberWithCommas(roundNumber(item?.totalMonthlyReturn))} ₪`,
  ]);

  const planADisposalboardtableData = {
    head: tableHead,
    body: tableBodyA,
  };

  const planBDisposalboardtableData = {
    head: tableHead,
    body: tableBodyB,
  };

  const planADetailsTable = {
    head: plansTableHead,
    body: [planADetails],
  };
  const planBDetailsTable = {
    head: plansTableHead,
    body: [planBDetails],
  };

  const actions = (
    <Button onClick={handleContinue} rightSection={<IconArrowNarrowLeft size={16} />}>{t('globals.continue')}</Button>
  );
  const side = <div></div>;

  return (

    <Page
      actions={actions}
      side={side}
      title={t('pages.summary.title')}
      sideSize="sm"
      subtitle={t('pages.summary.subtitle')}
      extraSize="xl"
    >

      <Page.Head
        title={t('pages.summary.title')}
        subtitle={t('pages.summary.subtitle')}
      />

      <div>
        <DataSummaryCard details={details} />
      </div>

      <div>
        <h3>{t('pages.summary.title.borrowerDetails')}</h3>
        <BorrowerDetailsTable borrowers={proposal.borrower} />
      </div>

      {disposalboard?.length > 0 && (
        <>
          <h3>{boxesTitles.boxATitle}</h3>
          <div className={classes.summaryContainer}>
            <TableSimple data={planADetailsTable} />
            <div className={classes.summaryCollapse}>
              <Page.Section
                title={`${t('pages.summary.title.disposalBoardFor')}${boxesTitles.boxATitle}`}
                withCollapse
                withBackground
                startClose>
                <TableSimple data={planADisposalboardtableData} />
              </Page.Section>
            </div>
          </div>
        </>
      )}

      {disposalboard?.length > 1 && (
        <>
          <h3>{boxesTitles.boxBTitle}</h3>
          <div className={classes.summaryContainer}>
            <TableSimple data={planBDetailsTable} />
            <div className={classes.summaryCollapse}>
              <Page.Section
                title={`${t('pages.summary.title.disposalBoardFor')}${boxesTitles.boxBTitle}`}
                withCollapse
                withBackground
                startClose>
                <TableSimple data={planBDisposalboardtableData} />
              </Page.Section>
            </div>
          </div>
        </>
      )}
    </Page>
  );
};

export default Summary;
